const LAST_ACTIVITY_KEY = "session_last_activity_at";
/** Legacy key from when idle was coupled to access-token expires_in (AB#11730). */
const SESSION_IDLE_TIMEOUT_MS_KEY = "session_idle_timeout_ms";

/** Default idle logout window: 30 minutes of no user activity. */
export const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60_000;

/** Product floor from AB#11730 — session must survive at least this long when idle. */
export const MIN_IDLE_TIMEOUT_MS = 15 * 60_000;

function getEnvNumber(key: string): number | null {
  const raw = (process.env as any)?.[key];
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function getConfiguredIdleTimeoutMs(): number | null {
  const ms = getEnvNumber("REACT_APP_IDLE_TIMEOUT_MS");
  if (ms !== null && ms > 0) return ms;

  const minutes = getEnvNumber("REACT_APP_IDLE_TIMEOUT_MINUTES");
  if (minutes !== null && minutes > 0) return minutes * 60_000;

  return null;
}

/**
 * Idle timeout for logout after inactivity.
 * Uses REACT_APP_IDLE_TIMEOUT_MS / REACT_APP_IDLE_TIMEOUT_MINUTES when set,
 * otherwise 30 minutes. Access-token expires_in is NOT used (AB#11730) —
 * JWT TTL and idle policy are independent.
 */
export function getIdleTimeoutMs(): number {
  const configured = getConfiguredIdleTimeoutMs();
  if (configured !== null) {
    // Allow short values only in non-production so local/QA debug stays possible;
    // production always honors the AB#11730 floor of 15 minutes.
    if (process.env.NODE_ENV === "production") {
      return Math.max(configured, MIN_IDLE_TIMEOUT_MS);
    }
    return configured;
  }
  return DEFAULT_IDLE_TIMEOUT_MS;
}

/**
 * Previously stored idle timeout from login `expires_in`. That coupled JWT TTL
 * to idle logout and caused early sign-outs (AB#11730). Kept as a no-op that
 * clears any legacy stored value so old sessions pick up the idle policy.
 */
export function setSessionIdleTimeoutFromExpiresIn(_expiresInSeconds: number): void {
  try {
    localStorage.removeItem(SESSION_IDLE_TIMEOUT_MS_KEY);
  } catch (_) {
    // ignore storage failures
  }
}

export function clearSessionIdleTimeout(): void {
  try {
    localStorage.removeItem(SESSION_IDLE_TIMEOUT_MS_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch (_) {
    // ignore
  }
}

export function getLastActivityAt(): number | null {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function markUserActivity(now: number = Date.now()): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    window.dispatchEvent(new Event("session-activity"));
  } catch (_) {
    // ignore storage failures (private mode, quota, etc.)
  }
}

export function isIdleTimedOut(now: number = Date.now()): boolean {
  const last = getLastActivityAt();
  if (!last) return false;
  return now - last > getIdleTimeoutMs();
}
